/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table } from 'antd';
import { nanoid } from 'nanoid';
import { editIcon, checkGreenCircle, deleteCirclePurple } from 'assets/images';
import ButtonText from 'components/forms/button-text';
import { InputText } from 'components/inputs';
import Message from 'components/message';
import { deleteLeadAsync, updateLeadAsync } from 'store/leads';
import { IdType, UpdateDataPayload } from 'types/common';
import { LeadStatus, Lead, LeadDto } from 'types/leads';
import { Dictionary } from 'utils/constants/dictionary';
import { formatDate } from 'utils/helpers/date-time';
import formatPhoneNumber from 'utils/helpers/format-phone-number';
import { getLeadStatusKey } from 'utils/helpers/get-lead-status-key';
import validatePhoneNumber from 'utils/helpers/validate-phone-number';
import { useAppDispatch } from 'utils/hooks';
import LeadStatusDropdown from '../lead-status-dropdown';
import './LeadBaseTable.scss';

type Props = {
	dataSource: Lead[];
};

const getStatusCn = (status: LeadStatus): string => {
	switch (status) {
		case LeadStatus.CONTACTED: return 'lead-status-btn_contacted';
		case LeadStatus.IN_NEGOTIATIONS: return 'lead-status-btn_in-negotiations';
		case LeadStatus.LINK_SENT: return 'lead-status-btn_sent';
		case LeadStatus.LOST: return 'lead-status-btn_lost';
		case LeadStatus.NEW_LEAD: return 'lead-status-btn_new-lead';
		case LeadStatus.POSTPONED: return 'lead-status-btn_postponed';
		case LeadStatus.SOLD: return 'lead-status-btn_sold';
		default: return '';
	}
};

const LeadBaseTable = ({ dataSource }: Props): JSX.Element | null => {
	const dispatch = useAppDispatch();

	const intl = useIntl();

	const [editing, setEditing] = useState<UpdateDataPayload<LeadDto> | null>(null);

	const isEditing = (record: Lead): boolean => editing?.id === record.id;

	const handleEdit = (record: Lead) => {
		setEditing({
			id: record.id,
			data: {
				email: record.email,
				name: record.name,
				phoneNumber: record.phoneNumber,
				status: record.status,
				comment: record.comment,
			},
		});
	};

	const handleSave = () => {
		if (editing) {
			dispatch(updateLeadAsync({
				...editing,
				data: { ...editing.data, phoneNumber: formatPhoneNumber(editing.data.phoneNumber) },
			}))
				.then(res => {
					if (res.payload) {
						Message.success({ messageId: Dictionary.message.success.changesSaved });
						setEditing(null);
					}
				});
		}
	};

	const handleDelete = (id: IdType) => {
		dispatch(deleteLeadAsync(id))
			.then(res => {
				if (res.payload) {
					Message.success({ messageId: Dictionary.message.success.leadDeleted });
					setEditing(null);
				}
			});
	};

	const onChangeEditing = (key: keyof LeadDto, value: string) => {
		if (editing) {
			setEditing({ ...editing, data: { ...editing.data, [key]: value } });
		}
	};

	const editingNameError: boolean = !!editing && !editing.data.name.length;
	const editingPhoneRequiredError: boolean = !!editing && !editing.data.phoneNumber.length;
	const editingPhoneValidationError: boolean = !!editing?.data.phoneNumber &&
	!validatePhoneNumber(editing.data.phoneNumber);

	const getPhoneErrorText = (): string | undefined => {
		if (editingPhoneRequiredError) return intl.formatMessage({
			defaultMessage: 'Required', id: Dictionary.validation.required,
		});

		if (editingPhoneValidationError) return intl.formatMessage({
			defaultMessage: 'Invalid phone number', id: Dictionary.validation.invalidPhone,
		});
	};

	const columns = [
		{
			title: () => <FormattedMessage defaultMessage="Lead name" id={Dictionary.leads.table.name} />,
			dataIndex: 'name',
			key: 'name',
			render: (name: string, record: Lead)=>
				!isEditing(record) ? name : (
					<InputText
						defaultPlaceholder="Input name"
						defaultValue={name}
						help={editingNameError
							? intl.formatMessage({ defaultMessage: 'Required', id: Dictionary.validation.required })
							: undefined
						}
						onChange={e => onChangeEditing('name', e.target.value)}
						placeholderId={Dictionary.leads.table.namePlaceholder}
						validateStatus={editingNameError ? 'error' : undefined}
					/>
				),
		},
		{
			title: () => <FormattedMessage defaultMessage="Lead contacts" id={Dictionary.leads.table.contacts} />,
			dataIndex: 'email',
			key: 'email',
			render: (email: string, record: Lead) => (
				<div className="leads-item-info">
					{!isEditing(record) ? (
						<div className="leads-item-info__phone">
							{record.phoneNumber}
						</div>
					) : (
						<InputText
							defaultPlaceholder="Input phone number"
							defaultValue={record.phoneNumber}
							help={getPhoneErrorText()}
							onChange={e => onChangeEditing('phoneNumber', e.target.value)}
							placeholderId={Dictionary.leads.table.phonePlaceholder}
							validateStatus={editingPhoneRequiredError || editingPhoneValidationError
								? 'error' : undefined
							}
						/>
					)}
					{!isEditing(record) ? (
						<div className="leads-item-info__email">
							{email}
						</div>
					) : (
						<InputText
							defaultPlaceholder="Input email"
							defaultValue={email}
							onChange={e => onChangeEditing('email', e.target.value)}
							placeholderId={Dictionary.leads.table.emailPlaceholder}
						/>
					)}
				</div>
			),
		},
		{
			title: () => <FormattedMessage defaultMessage="Status" id={Dictionary.leads.table.status} />,
			dataIndex: 'status',
			key: 'status',
			render: (status: LeadStatus, record: Lead) => (
				!isEditing(record) ? (
					<div className={`leads-item-status ${getStatusCn(status)}`}>
						<FormattedMessage
							defaultMessage="Lead status"
							id={getLeadStatusKey(status)}
						/>
					</div>
				) : (
					<LeadStatusDropdown
						btnDefaultText="Status"
						btnTextId={editing ? getLeadStatusKey(editing.data.status) : 'default-id'}
						highlightedBtns={editing?.data.status ? [editing.data.status] : undefined}
						onSelect={(value: LeadStatus) => {
							onChangeEditing('status', value);
						}}
					/>
				)
			),
		},
		{
			title: () => <FormattedMessage defaultMessage="Created" id={Dictionary.leads.table.created} />,
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (createdAt: string) => (
				<div className="leads-item-create">
					{formatDate(createdAt)}
				</div>
			),
		},
		{
			title: () => <FormattedMessage defaultMessage="Actions" id={Dictionary.leads.table.actions} />,
			dataIndex: 'id',
			key: 'id',
			render: (id: IdType, record: Lead) => (
				<>
					{!isEditing(record) ? (
						<ButtonText
							className="edit-btn"
							defaultText="Edit"
							disabled={!!editing && editing.id !== record.id}
							imgSrc={editIcon}
							onClick={() => handleEdit(record)}
							textId={Dictionary.common.edit}
						/>
					) : (
						<div className="leads-item-btns">
							<ButtonText
								className="leads-item-btn"
								defaultText="Save"
								disabled={editingNameError || editingPhoneRequiredError || editingPhoneValidationError}
								imgSrc={checkGreenCircle}
								onClick={handleSave}
								textId={Dictionary.common.save}
							/>
							<ButtonText
								className="leads-item-btn"
								defaultText="Delete"
								imgSrc={deleteCirclePurple}
								onClick={() => handleDelete(id)}
								textId={Dictionary.common.delete}
							/>
						</div>
					)}
				</>
			),
		},
	];

	const tableKey = useMemo(() => (dataSource.length) ? nanoid() : undefined, [dataSource.length]);

	return (
		<Table
			className="leads-table"
			columns={columns}
			dataSource={dataSource}
			expandable={dataSource.length ? {
				defaultExpandAllRows: true,
				expandedRowRender: (record: Lead) => (
					<div className="table-cell__expanded">
						{isEditing(record) ? (
							<InputText
								defaultPlaceholder="Input comment"
								defaultValue={record.comment ?? ''}
								onChange={e => onChangeEditing('comment', e.target.value)}
								placeholderId={Dictionary.leads.table.commentPlaceholder}
							/>
						) : (
							<p>
								{record.comment}
							</p>
						)}
					</div>
				),
				rowExpandable: record => !!record.comment,
				showExpandColumn: false,
			} : undefined}
			key={tableKey}
			pagination={false}
			rowKey="id"
			scroll={{ y: '50vh' }}
		/>
	);
};

export default LeadBaseTable;
